import type Feature from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import type { AttributeValue, CasurHydricClass, CasurHydricKind, CasurHydricReference, CasurHydricStatus, OriginalAttributes, VectorFeatureRecord, VectorLayerModel } from '../core/types';
import { createGenericLayer } from '../domain/layers';
import { inspectShapefiles, type ManualShapefileCrs } from '../io/shapefileImport';

export class CasurHydricImportError extends Error {
  constructor(message: string) { super(message); this.name = 'CasurHydricImportError'; }
}

const normalized = (value: unknown): string => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const compact = (value: unknown): string => normalized(value).replace(/\s/g, '');

const schemaScore = (layer: VectorLayerModel, kind: CasurHydricKind): number => {
  const name = normalized(`${layer.name} ${layer.sourceName}`);
  const fields = layer.attributeFields.map(compact);
  if (kind === 'channels') {
    return (/canal/.test(name) ? 8 : 0) + (/riego/.test(name) ? 5 : 0)
      + fields.filter((field) => /canal|riego|sector|status|estado/.test(field)).length;
  }
  return (/dren/.test(name) ? 9 : 0)
    + fields.filter((field) => /dren|coddr|finca|orden|tipo/.test(field)).length;
};

export const identifyHydricLayers = (layers: readonly VectorLayerModel[]): { channels: VectorLayerModel; drainage: VectorLayerModel } => {
  if (layers.length < 2) throw new CasurHydricImportError('El ZIP debe contener las redes de canales y drenajes CASUR.');
  let choice: { channels: VectorLayerModel; drainage: VectorLayerModel; score: number } | null = null;
  layers.forEach((channels) => layers.forEach((drainage) => {
    if (channels === drainage) return;
    const channelScore = schemaScore(channels, 'channels');
    const drainageScore = schemaScore(drainage, 'drainage');
    const score = channelScore + drainageScore;
    if (channelScore > 0 && drainageScore > 0 && (!choice || score > choice.score)) choice = { channels, drainage, score };
  }));
  if (!choice) throw new CasurHydricImportError('No fue posible identificar con seguridad las capas de canales y drenajes por nombre o estructura de campos.');
  const selected = choice as { channels: VectorLayerModel; drainage: VectorLayerModel; score: number };
  if (layers.some((layer) => layer !== selected.channels && layer !== selected.drainage && Math.max(schemaScore(layer, 'channels'), schemaScore(layer, 'drainage')) >= selected.score / 2)) {
    throw new CasurHydricImportError('La identificación de canales y drenajes es ambigua. Revisa los nombres y campos del ZIP.');
  }
  return { channels: selected.channels, drainage: selected.drainage };
};

const entriesFor = (attributes: OriginalAttributes, pattern: RegExp): [string, AttributeValue][] => {
  const entries = Object.entries(attributes) as [string, AttributeValue][];
  const preferred = entries.filter(([key]) => pattern.test(compact(key)));
  return preferred.length ? preferred : entries;
};

const firstValue = (attributes: OriginalAttributes, pattern: RegExp): string => {
  const entry = (Object.entries(attributes) as [string, AttributeValue][]).find(([key, value]) => pattern.test(compact(key)) && String(value ?? '').trim());
  return entry ? String(entry[1]).trim() : '';
};

export const hydricClassFor = (attributes: OriginalAttributes): CasurHydricClass => {
  const values = entriesFor(attributes, /clase|categoria|tipo|orden|jerarquia|nivel/).map(([, value]) => normalized(value));
  if (values.some((value) => /(^| )(principal|primari[oa]|orden 1|clase 1)( |$)/.test(value) || value === '1')) return 'principal';
  if (values.some((value) => /(^| )(secundari[oa]|orden 2|clase 2)( |$)/.test(value) || value === '2')) return 'secondary';
  if (values.some((value) => /(^| )(terciari[oa]|orden 3|clase 3)( |$)/.test(value) || value === '3')) return 'tertiary';
  return 'unclassified';
};

export const hydricStatusFor = (attributes: OriginalAttributes): CasurHydricStatus => {
  const values = entriesFor(attributes, /status|estado|condicion|situacion|proyect|exist/).map(([, value]) => normalized(value));
  if (values.some((value) => /proyect|propuest|futur|planific/.test(value))) return 'projected';
  if (values.some((value) => /exist|actual|construid|operativ|activo/.test(value))) return 'existing';
  return 'unknown';
};

const geometryIsValidLine = (geometry: Geometry | undefined): boolean => {
  if (!geometry || (geometry.getType() !== 'LineString' && geometry.getType() !== 'MultiLineString')) return false;
  const candidate = geometry as Geometry & { getFlatCoordinates?: () => number[]; getLength?: () => number };
  const coordinates = candidate.getFlatCoordinates?.() ?? [];
  return coordinates.length >= 4 && coordinates.every(Number.isFinite) && Number(candidate.getLength?.() ?? 0) > 0;
};

const stableRecords = (layer: VectorLayerModel, kind: CasurHydricKind): { records: VectorFeatureRecord[]; warnings: string[] } => {
  const warnings: string[] = [];
  let unclassified = 0; let unknownStatus = 0; let emptyFields = 0;
  const seen = new Set<string>(); let duplicates = 0;
  const records = layer.features.map((record, index) => {
    const geometry = record.feature.getGeometry();
    if (!geometryIsValidLine(geometry)) throw new CasurHydricImportError(`${layer.name}: la entidad ${index + 1} no contiene una geometría lineal válida.`);
    const signature = JSON.stringify((geometry as Geometry & { getFlatCoordinates?: () => number[] }).getFlatCoordinates?.() ?? []);
    if (seen.has(signature)) duplicates += 1; else seen.add(signature);
    const feature = record.feature.clone() as Feature<Geometry>;
    const id = `casur-hydric:${kind}:${index + 1}`;
    const hydricClass = hydricClassFor(record.attributes); const status = kind === 'channels' ? hydricStatusFor(record.attributes) : 'unknown';
    if (hydricClass === 'unclassified') unclassified += 1;
    if (kind === 'channels' && status === 'unknown') unknownStatus += 1;
    emptyFields += Object.values(record.attributes).filter((value) => value === null || String(value).trim() === '').length;
    feature.set('casurHydricKind', kind, true); feature.set('casurHydricId', id, true); feature.set('casurHydricClass', hydricClass, true); feature.set('casurHydricStatus', status, true);
    feature.set('casurHydricLabel', firstValue(record.attributes, kind === 'channels' ? /nombre|name|nomcanal|canal/ : /coddren|codigo|code|nombre/), true);
    return { id, feature, attributes: record.attributes };
  });
  if (unclassified) warnings.push(`${layer.name}: ${unclassified} entidad(es) quedaron Sin clasificar.`);
  if (unknownStatus) warnings.push(`${layer.name}: ${unknownStatus} canal(es) no indican si son existentes o proyectados.`);
  if (emptyFields) warnings.push(`${layer.name}: se conservaron ${emptyFields} valor(es) de atributo vacíos.`);
  if (duplicates) warnings.push(`${layer.name}: se detectaron ${duplicates} geometría(s) duplicadas; se conservaron sin cambios.`);
  return { records, warnings };
};

const normalizedLayer = (layer: VectorLayerModel, kind: CasurHydricKind): { layer: VectorLayerModel; warnings: readonly string[] } => {
  if (!layer.features.length) throw new CasurHydricImportError(`${layer.name}: la capa está vacía.`);
  const invalidTypes = layer.geometryTypes.filter((type) => type !== 'LineString' && type !== 'MultiLineString');
  if (invalidTypes.length) throw new CasurHydricImportError(`${layer.name}: se esperaban líneas, pero contiene ${invalidTypes.join(', ')}.`);
  const result = stableRecords(layer, kind);
  return {
    layer: { ...createGenericLayer({ id: `casur-hydric:${kind}`, name: kind === 'channels' ? 'Canales de riego CASUR' : 'Red de drenajes CASUR', sourceName: layer.sourceName, sourceFormat: 'SHP', sourceCrs: layer.sourceCrs, assumedCrs: layer.assumedCrs, records: result.records, warnings: [...layer.warnings, ...result.warnings] }), locked: true },
    warnings: result.warnings,
  };
};

const sha256 = async (file: File): Promise<string> => {
  const digest = await globalThis.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

export async function importCasurHydricReference(file: File, fallbackCrs?: ManualShapefileCrs): Promise<CasurHydricReference> {
  if (!/\.zip$/i.test(file.name)) throw new CasurHydricImportError('Selecciona el ZIP local que contiene los Shapefiles de canales y drenajes CASUR.');
  const { datasets } = await inspectShapefiles([file], fallbackCrs ? { fallbackCrs } : {});
  // General layers may come without .dbf; the hydric reference classifies by attributes, so it still requires them.
  const withoutDbf = datasets.find(({ summary }) => !summary.hasDbf);
  if (withoutDbf) throw new CasurHydricImportError(`Falta el archivo .dbf para ${withoutDbf.summary.name}. La Referencia CASUR necesita los atributos de canales y drenajes.`);
  const imported = datasets.map(({ layer }) => layer);
  const identified = identifyHydricLayers(imported);
  const channels = normalizedLayer(identified.channels, 'channels');
  const drainage = normalizedLayer(identified.drainage, 'drainage');
  const warnings = [...identified.channels.warnings, ...identified.drainage.warnings, ...channels.warnings, ...drainage.warnings];
  return {
    channels: channels.layer,
    drainage: drainage.layer,
    metadata: {
      sourceFileName: file.name,
      importedAt: new Date().toISOString(),
      fingerprint: await sha256(file),
      sourceCrs: [...new Set([identified.channels.sourceCrs, identified.drainage.sourceCrs])],
      channelCount: channels.layer.features.length,
      drainageCount: drainage.layer.features.length,
      warnings,
      schemaVersion: 1,
    },
  };
}
